import React from 'react';
import {
	Col,
	Grid,
	Row,
} from 'antd';
import {
	Currencies,
	LaytimeTerms,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { CargoDetails, GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { UpdateCargoRequest } from '@api/features/cargos/updateCargo';
import { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import EstimatedItemsCard from '@client/screens/cargos/components/EstimatedItemsCard/EstimatedItemsCard';
import {
	UpdateCargoPort,
} from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import CargoKeyDetails from './CargoKeyDetails';
import { CargoPortsDetails } from './CargoPortsDetails';
import LaytimeDetails from './LaytimeDetails';
import { ModalModes } from './CargoDetailsScreen';

type CargoDetailsContentProps = {
	mode: ModalModes;
	isTceLocked?: boolean;
	estimate: GetEstimateDetailsResponse | null;
	selectedCargoPortId?: number | null;
	inEstimate: boolean;
	renderKey: number;
	cargoDetails: GetCargoDetailsResponse | CargoDetails | undefined;
	baseCurrency: Values<typeof Currencies>;
	laytimeTerms: LaytimeTerms;
	cargoPorts: CargoPortWithEstimatedValues[] | null | undefined;
	sortedCargoPorts: CargoPortWithEstimatedValues[];
	quantitiesRemaining: {
		loadedRemaining: number;
		dischargeRemaining: number;
	} | null;
	handleCargoChange: (
		cargoId: number,
		attributes: UpdateCargoRequest['attributes'],
	) => void;
	handleCargoPortChange: (
		values: UpdateCargoPort,
	) => void;
	onUpdateCargo: (
		cargoId: number,
		attributes: UpdateCargoRequest['attributes'],
	) => void;
	copyCargoPortSettings: (cargoPortId: number) => void;
	onUpdateEstimateCargo?: (
		cargoId: number,
		attributes: UpdateCargoRequest['attributes'],
	) => void;
	onUpdateEstimateCargoPort?: (
		values: UpdateCargoPort,
	) => void;
};

const CargoDetailsContent = ({
	mode,
	isTceLocked,
	estimate,
	selectedCargoPortId,
	inEstimate,
	renderKey,
	cargoDetails,
	baseCurrency,
	laytimeTerms,
	cargoPorts,
	sortedCargoPorts,
	quantitiesRemaining,
	handleCargoChange,
	handleCargoPortChange,
	onUpdateEstimateCargo,
	onUpdateEstimateCargoPort,
	copyCargoPortSettings,
}: CargoDetailsContentProps) => {
	const screens = Grid.useBreakpoint();

	return (
		<Row gutter={[16, 16]}>
			<Col span={screens.lg ? 12 : 24}>
				<CargoKeyDetails
					key={renderKey}
					refreshCargoDetails={() => null}
					handleChange={inEstimate && onUpdateEstimateCargo ? onUpdateEstimateCargo : handleCargoChange}
					cargoDetails={cargoDetails}
					isTceLocked={isTceLocked}
					baseCurrency={baseCurrency}
					mode={mode}
					estimate={estimate}
				/>
			</Col>
			<Col span={screens.lg ? 12 : 24}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<LaytimeDetails
							mode={mode}
							cargoDetails={cargoDetails}
							laytimeTerms={laytimeTerms}
							handleChange={inEstimate && onUpdateEstimateCargo ? onUpdateEstimateCargo : handleCargoChange}
						/>
					</Col>
					<Col span={24}>
						<CargoPortsDetails
							selectedCargoPortId={selectedCargoPortId}
							mode={mode}
							handleChange={inEstimate && onUpdateEstimateCargoPort ? onUpdateEstimateCargoPort : handleCargoPortChange}
							cargoPorts={cargoPorts ?? []}
							sortedCargoPorts={sortedCargoPorts}
							cargoDetails={cargoDetails}
							laytimeTerms={laytimeTerms}
							quantitiesRemaining={quantitiesRemaining}
							refreshDetails={() => null}
							copyCargoPortSettings={copyCargoPortSettings}
						/>
					</Col>
					{inEstimate && onUpdateEstimateCargo && (
						<Col span={24}>
							<EstimatedItemsCard
								handleChange={onUpdateEstimateCargo}
								cargoDetails={cargoDetails}
							/>
						</Col>
					)}
				</Row>
			</Col>
		</Row>
	);
};

export default CargoDetailsContent;
